import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'react-recompose';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import gql from 'graphql-tag';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { graphql } from '@apollo/react-hoc';

import MediaCarousel from '../../../../components/MediaCarousel';
import ImageWithFallback from '../../../../components/ImageWithFallback';
import Header from '../../../../components/Header';
import Icon from '../../../../components/Icon';
import HasRole from '../../../../components/HasRole';
import PremiumHemSubscription from '../../../../components/PremiumHemSubscription';
import LockImage from '../../../../components/images/ico-lock-x16.png';
import Image from '../../../../components/Image';
import Button from '../../../../components/Button';

import { SimpleLoadingComponent } from '../../../../utils/apollo';
import messages from '../../messages';
import * as constants from '../../../Authorization/constants';
import * as actions from '../../actions';
import '../../styles.css';

export class MediaPageComponent extends React.PureComponent {
  static propTypes = {
    viewer: PropTypes.object.isRequired,
    toggleUpgradeFormVisibility: PropTypes.func.isRequired,
    file: PropTypes.string,
    media: PropTypes.array,
    hasRegisteredChanges: PropTypes.object,
    initializeMedia: PropTypes.func,
  };

  componentDidMount = () => {
    const { media, hasRegisteredChanges } = this.props;
    // get data from api viewer
    const hubsWebTourMedia = get(this.props, 'viewer.hubsWebTour.campus_media.assets', []);
    const productId = get(this.props, 'viewer.hubsWebTour.campus_media.product_id');

    // is we get data from api we set that data to state
    if (isEmpty(media) && !hasRegisteredChanges && hubsWebTourMedia) {
      // NOTE: using slice(0, 31) below to account for cases when the CMS data has more images than what the UI allows (currently allows 31)
      this.props.initializeMedia({
        media: hubsWebTourMedia.slice(0, 31),
        hasRegisteredChanges: false,
        isNewMedia: hubsWebTourMedia.length === 0,
        productId,
      });
    }
  };

  render() {
    const { viewer, toggleUpgradeFormVisibility, file, media } = this.props;
    return (
      <React.Fragment>
        <Header as="h1" styleName="media-header">
          <FormattedMessage {...messages.mediaHeader} />
        </Header>
        <Header.Subheader>
          <FormattedMessage {...messages.mediaSubheader} />
        </Header.Subheader>
        <div styleName="content-section">
          <Header as="h2" styleName="header-margin-top media-subheader">
            <FormattedMessage {...messages.logoHeader} />
          </Header>
          <div styleName="media-subblurb">
            <ImageWithFallback
              styleName="sidebar-college-logo"
              src={file || get(viewer, 'hubsCollegeLogo.college_logo.logoDisplayUrl')}
              data-cy="Premium-Hem-Media-College-Logo"
              altSrc={`https://static.naviance.com/libraries/college-logos/${viewer.heInstitution}.gif`}
              fallback={(
                <div>
                  <div styleName="media-nologo">No Logo Uploaded.</div>
                  <Icon
                    name="building outline"
                    color="grey"
                    size="huge"
                    style={{
                      boxSizing: 'border-box',
                      border: '1px solid grey',
                      padding: '20px',
                      width: 'auto',
                      height: 'auto',
                    }}
                  />
                </div>
              )}
              alt="College Logo"
            />
          </div>
        </div>
        <HasRole anyOf={[constants.ADMIN_ROLE, constants.PUBLISHING_ROLE]}>
          <PremiumHemSubscription>
            <Header as="h2" styleName="photos-and-videos-header">
              <FormattedMessage {...messages.photosAndVideosHeader} />
            </Header>
            <Header.Subheader styleName="content-margin-bottom"><FormattedMessage {...messages.addUpToPhotosAndVideos} /></Header.Subheader>
            <div styleName="media-component-container">
              <MediaCarousel
                media={media}
              />
            </div>
          </PremiumHemSubscription>
          <PremiumHemSubscription inactive>
            <div styleName="content">
              <div styleName="photos-videos-locked">
                <Header as="h2" styleName="content-heading photos-videos-locked-header">
                  Photos and Videos
                </Header>
                { /* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */ }
                <span styleName="premium-feature-photos" onClick={toggleUpgradeFormVisibility}>
                  <FormattedMessage {...messages.premiumFeatureLink} />
                  <Image src={LockImage} styleName="lock-image" aria-hidden="true" />
                </span>
              </div>
              <br />
              <div styleName="photos-upgrade-subheading">
                Create an engaging photo and video gallery.
              </div><br /><br />
              <Button secondary styleName="upgrade-button" onClick={toggleUpgradeFormVisibility}>Upgrade</Button><br /><br /><br />
              <div styleName="intro-paragraph">
                With <strong>Photos and Videos</strong>, your institution can
                display up to 31 photos and videos on your college profile.
                Each piece of media allows for an optional caption to enrich
                your institution&#39;s gallery.
              </div>
            </div>
          </PremiumHemSubscription>
        </HasRole>
      </React.Fragment>
    );
  }
}

const query = gql`
  query CollegeProfileMediaPage {
    viewer {
      id
      heInstitution
      hubsCollegeLogo {
        college_logo {
          logoUri
          logoDisplayUrl
        }
      }
      hubsWebTour {
        campus_media {
          active
          date_from
          date_to
          product_id
          assets{
              title
              order
              caption
              media_type
              media_url
              mediaDisplayUrl
            }
          }
      }
    }
  }
`;

const mapStateToProps = (state) => ({
  file: get(state, 'nonPersistedCollegeProfile.media.file'),
  media: get(state, 'nonPersistedCollegeProfile.media.media'),
});

export default compose(
  connect(mapStateToProps, { ...actions }),
  SimpleLoadingComponent(graphql(query, {
    options: { fetchPolicy: 'network-only' },
  })),
)(MediaPageComponent);
